window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.trackMaxHeightValue = function (currentFrameMax, heightValueFn) {
    let maxHeightValue = currentFrameMax;

    if ((!this.maxHeightValue || this.maxHeightValue <= 0) && this.currentData && this.currentData.intervals) {
        const results = this._cachedGlobalMax || this.calculateGlobalMaxHeightValue(heightValueFn);
        this._cachedGlobalMax = results;
        this.maxHeightValue = results.max;
        if (results.hasNegative) this.hasNegativeGlobal = true;
    }

    if (this._cachedGlobalMax) {
        if (this._cachedGlobalMax.max > maxHeightValue) {
            maxHeightValue = this._cachedGlobalMax.max;
        }
        if (this._cachedGlobalMax.hasNegative) {
            this.hasNegativeGlobal = true;
        }
    }

    if (this.maxHeightValue && this.maxHeightValue > maxHeightValue) {
        maxHeightValue = this.maxHeightValue;
    }

    this.maxHeightValue = maxHeightValue;
    return maxHeightValue;
};

window.StockCubeCore.resolveNegativeValues = function (allHeightValues) {
    const currentFrameHasNegative = allHeightValues.some(val => val < 0);
    if (currentFrameHasNegative) this.hasNegativeGlobal = true;

    let hasNegativeValues = this.hasNegativeGlobal || currentFrameHasNegative;
    if (['top-gainers', 'top-gainers-sec', 'top-losers', 'top-losers-sec'].includes(this.data.dataSource)) {
        hasNegativeValues = false;
    }
    return hasNegativeValues;
};

window.StockCubeCore.computeHeightScale = function (targetHeight, maxHeightValue, hasNegativeValues) {
    return targetHeight / (maxHeightValue || 1) / (hasNegativeValues ? 2 : 1);
};

window.StockCubeCore.buildSectorMapping = function (sectorIds, numColumns) {
    const sectorIdToColIndex = new Map();
    const sectorColors = new Map();
    sectorIds.forEach((sectorId, index) => {
        sectorIdToColIndex.set(sectorId, index);
        const hue = Math.round((index * 360 / numColumns) % 360);
        sectorColors.set(sectorId, `hsl(${hue}, 80%, 50%)`);
    });
    return { sectorIdToColIndex, sectorColors };
};

window.StockCubeCore.computeSectorAverages = function (stockBars) {
    const sectorAverages = new Map();
    stockBars.forEach((barInfo) => {
        const sectorId = barInfo.sectorId;
        if (sectorId) {
            const currentAvg = sectorAverages.get(sectorId) || { sum: 0, count: 0 };
            sectorAverages.set(sectorId, {
                sum: currentAvg.sum + barInfo.heightValue,
                count: currentAvg.count + 1
            });
        }
    });
    return sectorAverages;
};

window.StockCubeCore.computeBarYPositions = function (height, zeroPlane, labelYOffset, targetHeight) {
    const absHeight = Math.min(targetHeight, Math.max(0.001, Math.abs(height)));
    const isPositive = height >= 0;
    const barBaseY = isPositive ? zeroPlane + (absHeight / 2) : zeroPlane - (absHeight / 2);
    const primaryLabelY = isPositive ? zeroPlane + absHeight + labelYOffset : zeroPlane - absHeight - labelYOffset;
    return { absHeight, isPositive, barBaseY, primaryLabelY };
};

window.StockCubeCore.applyGradientColorAnimations = function (bar, color) {
    bar.setAttribute('animation__color', {
        property: 'color',
        to: color,
        dur: 400,
        easing: 'easeInOutQuad'
    });
    bar.setAttribute('animation__material', {
        property: 'material.color',
        to: color,
        dur: 400,
        easing: 'easeInOutQuad'
    });
};

window.StockCubeCore.resolveBarColor = function (heightValue, colorMode, sectorId, sectorColors, getGradientColorFn) {
    if (colorMode === 'gradient') {
        return getGradientColorFn(heightValue);
    } else if (colorMode === 'sector') {
        return sectorColors.get(sectorId) || '#FFFFFF';
    }
    throw new Error(`Invalid colorMode: ${colorMode}. Must be 'gradient' or 'sector'.`);
};

window.StockCubeCore.buildFixedSectorPositionMap = function (stockBars) {
    const positionMap = new Map();
    for (const [ticker, barInfo] of stockBars) {
        positionMap.set(ticker, { x: barInfo.x, z: barInfo.z });
    }
    return positionMap;
};

window.StockCubeCore.buildFixedGridPositionMap = function (stockBars, barWidth, spacing, gridSize) {
    const positionMap = new Map();
    const unit = barWidth + spacing;
    for (const [ticker, barInfo] of stockBars) {
        const col = Math.round(barInfo.x / unit + gridSize / 2 - 0.5);
        const correctedX = unit * (col - gridSize / 2 + 0.5);
        positionMap.set(ticker, { x: correctedX, z: barInfo.z });
    }
    return positionMap;
};

window.StockCubeCore.computeGridLayout = function (filteredCount) {
    const gridSize = 10;
    const maxRows = 5;
    const totalBars = Math.min(filteredCount, 50);
    return { gridSize, maxRows, totalBars };
};

window.StockCubeCore.removeOrphanedBars = function (stockBars, validTickers, container) {
    for (const ticker of Array.from(stockBars.keys())) {
        if (!validTickers.has(ticker)) {
            const barInfo = stockBars.get(ticker);
            if (barInfo && barInfo.bar && container.contains(barInfo.bar)) {
                container.removeChild(barInfo.bar);
            }
            if (barInfo && barInfo.primaryLabel && container.contains(barInfo.primaryLabel)) {
                container.removeChild(barInfo.primaryLabel);
            }
            stockBars.delete(ticker);
        }
    }
};

window.StockCubeCore.registerBarHoverEffect = function (bar, primaryLabel, originalFontSize, hoverEnabledInitially) {
    const hoverFontSize = originalFontSize * 1.5;

    if (typeof hoverEnabledInitially !== 'undefined') {
        bar._hoverEnabled = hoverEnabledInitially;
    }

    bar.addEventListener('mouseenter', () => {
        if (bar._hoverEnabled === false) return;
        if (!primaryLabel) return;
        primaryLabel.setAttribute('animation__fontsize', {
            property: 'font-size',
            to: hoverFontSize,
            dur: 300,
            easing: 'easeOutQuad'
        });
    });

    bar.addEventListener('mouseleave', () => {
        if (bar._hoverEnabled === false) return;
        if (!primaryLabel) return;
        primaryLabel.setAttribute('animation__fontsize', {
            property: 'font-size',
            to: originalFontSize,
            dur: 300,
            easing: 'easeInQuad'
        });
    });
};

window.StockCubeCore.computeHeightValuesFromBars = function (stockBars, stockMap, heightValueFn) {
    return Array.from(stockBars.values()).map(bar => {
        const stock = stockMap.get(bar.lastLabel.split('\n')[0]);
        return stock ? heightValueFn(stock) : bar.heightValue;
    });
};
